import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  CalendarCheck,
  CheckCircle,
  Eye,
  FileWarning,
  Play,
  RefreshCw,
  ScanSearch,
  ShieldCheck,
  XCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import PageHeader from '../components/PageHeader';
import ReviewDrawer, { docLabel } from '../components/ReviewDrawer';
import { useReviewInbox } from '../components/ReviewInboxContext';
import { createBlobRegistry } from '../lib/blobRegistry.mjs';
import { ErrorBox } from '../ui/kit';
import { useAuth } from '../App';
import './Ablaufdaten.css';
import { datumUndZeit, nurDatum } from '../lib/zeit';

// FAHRER-KI-BACKEND-001 (Teil B) — „Ablaufdaten prüfen".
// Die Erkennung liest, was auf dem Dokument steht; ÜBERNOMMEN wird ein Datum nur per Klick.
// Jede Übernahme/Handkorrektur wird protokolliert und lässt den Ablauf-Wächter sofort neu rechnen.
// FAHRER-KI-ABLAUFDATUM-PAAR-001: Führerschein/P-Schein haben EIN Datum für beide Seiten — die KI findet es auf
// einer Seite, die Übernahme setzt beide. Zwei verschiedene gelesene Daten = Konflikt, der Mensch entscheidet.

export const GROUPS = [
  { key: 'mismatch', label: 'Abweichungen', hint: 'Eingetragen ≠ auf dem Dokument', tone: 'critical', statuses: ['mismatch'] },
  { key: 'conflict', label: 'Zwei Daten erkannt', hint: 'Vorder- und Rückseite widersprechen sich', tone: 'critical', statuses: ['conflict'] },
  { key: 'detected_only', label: 'Gelesen, nichts eingetragen', hint: 'Datum auf dem Dokument, Feld leer', tone: 'warning', statuses: ['detected_only'] },
  { key: 'unreadable', label: 'Nicht lesbar', hint: 'Neu-Upload anstoßen oder von Hand', tone: 'warning', statuses: ['unreadable', 'error', 'unsupported'] },
  { key: 'unread', label: 'Noch nicht gelesen', hint: 'Vom Lauf noch nicht erfasst', tone: 'neutral', statuses: ['unread'] },
  { key: 'match', label: 'Übereinstimmend', hint: 'In Ordnung, bestätigt oder ohne Ablaufpflicht', tone: 'ok', statuses: ['match', 'confirmed', 'no_expiry'] },
];

export const STATUS_TEXT = {
  mismatch: 'Abweichung', conflict: 'Zwei Daten erkannt', detected_only: 'Nichts eingetragen', unreadable: 'Datum nicht lesbar', error: 'Lesen fehlgeschlagen',
  unsupported: 'PDF – nicht lesbar', unread: 'Noch nicht gelesen', match: 'Stimmt überein', confirmed: 'Von Hand bestätigt', no_expiry: 'Kein Ablaufdatum nötig', not_readable: 'Wird nicht gelesen',
};

export const SUSPENSION_TEXT = (reason) => {
  if (!reason) return null;
  if (reason.startsWith('dokument_abgelaufen:')) return 'gesperrt (Dokument abgelaufen)';
  if (reason.startsWith('dokument_gesperrt:')) return 'gesperrt (Compliance)';
  if (reason.startsWith('admin_gesperrt:')) return 'gesperrt (Admin)';
  if (reason.startsWith('selfie_abweichung:')) return 'gesperrt (Selfie)';
  return 'gesperrt';
};

const fmtDate = (iso) => nurDatum(iso, { leer: '–' });
const SIDE = { front: 'Vorderseite', back: 'Rückseite' };
// Paar-Zeile unter dem Vergleich: was steht auf der anderen Seite (eingetragen / gelesen)?
export function pairLine(rec) {
  const pair = rec && rec.pair;
  if (!pair) return null;
  if (!pair.other_document_id) return `${SIDE[pair.other_side]} fehlt noch — das Datum gilt später für beide Seiten.`;
  if (rec.status === 'conflict' && rec.conflict) return `Konflikt: hier ${fmtDate(rec.conflict.this_side)} gelesen, auf der ${SIDE[pair.other_side]} ${fmtDate(rec.conflict.other_side)} — bitte von Hand entscheiden.`;
  const gelesen = pair.other_detected ? fmtDate(pair.other_detected) : (pair.other_read ? 'kein Datum' : 'noch nicht gelesen');
  return `${SIDE[pair.other_side]}: eingetragen ${fmtDate(pair.other_entered)} · gelesen ${gelesen}. Ein Datum gilt für beide Seiten.`;
}
const fmtTime = (value) => datumUndZeit(value, { jahr: 'voll', leer: '–' });

export function groupOf(status) {
  return GROUPS.find((g) => g.statuses.includes(status)) || null;
}

function Thumb({ documentId, registry, hasAsset }) {
  const [state, setState] = useState({ url: null, failed: !hasAsset });
  useEffect(() => {
    let alive = true;
    if (!hasAsset) return undefined;
    api.get(`/admin/documents/${documentId}/preview`, { responseType: 'blob' })
      .then((response) => { if (alive) setState({ url: registry.register(documentId, response.data), failed: false }); })
      .catch(() => { if (alive) setState({ url: null, failed: true }); });
    return () => { alive = false; };
  }, [documentId, hasAsset, registry]);
  if (state.failed) return <div className="ablauf-thumb is-missing"><FileWarning size={16} /><span>Kein Bild</span></div>;
  if (!state.url) return <div className="ablauf-thumb vg-sk" />;
  return <div className="ablauf-thumb"><img src={state.url} alt="Dokument" /></div>;
}

function Row({ item, registry, canManage, onChanged, onOpen }) {
  const [manual, setManual] = useState('');
  const [manualOpen, setManualOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const rec = item.recognition;
  const doc = item.document;

  const call = async (label, fn, { confirmedLabel = null } = {}) => {
    setBusy(true);
    try {
      const response = await fn();
      const recheck = response?.data?.recheck;
      // „Datum von Hand" mit dem eingetragenen Wert = Bestätigung durch den Menschen — gilt immer, Zeile erledigt.
      const shown = response?.data?.confirmed && confirmedLabel ? confirmedLabel : label;
      toast.success(recheck && recheck.changed && recheck.suspended === false ? `${shown} — Sperre gelöst` : shown);
      setManualOpen(false); setManual('');
      onChanged();
    } catch (error) {
      toast.error(error.response?.data?.error || `${label} fehlgeschlagen`);
    } finally { setBusy(false); }
  };

  const applyDetected = () => call(rec.pair && rec.pair.other_document_id ? 'Datum für beide Seiten übernommen' : 'Datum übernommen', () => api.post(`/admin/documents/${doc.id}/expiry/apply-detected`));
  const saveManual = () => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(manual)) { toast.error('Datum als JJJJ-MM-TT eingeben'); return; }
    return call('Datum gesetzt', () => api.patch(`/admin/documents/${doc.id}/expiry`, { expires_at: manual }), { confirmedLabel: rec.pair && rec.pair.other_document_id ? 'Datum für beide Seiten bestätigt' : 'Datum bestätigt' });
  };
  const reread = () => call('Neu gelesen', () => api.post(`/admin/documents/${doc.id}/recognize`));

  return (
    <article className={`ablauf-row is-${rec.status}`} data-document-id={doc.id}>
      <Thumb documentId={doc.id} registry={registry} hasAsset={!!doc.has_private_asset} />
      <div className="ablauf-row-main">
        <div className="ablauf-row-head">
          <strong>{item.driver.name || 'Unbekannter Fahrer'}</strong>
          {item.driver.partner_company && <span className="ablauf-partner">{item.driver.partner_company}</span>}
          {item.driver.suspended_reason && <span className="ablauf-pill is-critical"><XCircle size={11} /> {SUSPENSION_TEXT(item.driver.suspended_reason)}</span>}
          <span className={`ablauf-pill is-${rec.status}`}>{STATUS_TEXT[rec.status] || rec.status}</span>
        </div>
        <div className="ablauf-row-doc">
          <span>{item.pair_merged ? docLabel(doc.document_type).replace(/ – Vorderseite$/, ' – Vorder- + Rückseite') : docLabel(doc.document_type)}</span>
          <small>r{doc.revision || 1} · {doc.lifecycle_status === 'pending_review' ? 'in Prüfung' : doc.lifecycle_status === 'expired' ? 'abgelaufen' : 'freigegeben'}{item.expired ? ' · Datum vorbei' : ''}</small>
        </div>
        <div className="ablauf-compare">
          <div><small>eingetragen</small><strong>{fmtDate(rec.entered)}</strong></div>
          <div><small>{rec.detected_source === 'other_side' ? `auf der ${SIDE[rec.pair.other_side]}` : 'auf dem Dokument'}</small><strong className={rec.status === 'mismatch' || rec.status === 'conflict' ? 'is-critical' : ''}>{rec.detected ? fmtDate(rec.detected) : (rec.status === 'unread' ? '–' : rec.status === 'conflict' ? 'zwei Daten' : rec.status === 'confirmed' ? '–' : 'nicht lesbar')}</strong></div>
          {rec.status === 'confirmed' && <div><small>von Hand</small><strong>bestätigt{rec.manual && rec.manual.at ? ` ${fmtTime(rec.manual.at)}` : ''}</strong></div>}
          {rec.read_at && <div><small>gelesen</small><strong>{fmtTime(rec.read_at)}</strong></div>}
        </div>
        {rec.slot && rec.slot.hint && <p className="ablauf-slot-hint"><AlertTriangle size={12} /> {rec.slot.hint}</p>}
        {item.pair_merged ? (
          <p className="ablauf-pair-hint" data-testid="pair-line">Auf beiden Seiten kein Datum gelesen — „Datum von Hand" gilt für beide Seiten.</p>
        ) : pairLine(rec) && <p className={`ablauf-pair-hint ${rec.status === 'conflict' ? 'is-critical' : ''}`} data-testid="pair-line">{pairLine(rec)}</p>}
        {rec.status === 'error' && rec.error_code && <p className="ablauf-slot-hint">Lesen fehlgeschlagen ({rec.error_code}) — beim nächsten Lauf erneut.</p>}
      </div>
      <div className="ablauf-row-actions">
        {canManage && rec.detected && rec.status !== 'match' && rec.status !== 'confirmed' && (
          <button className="is-primary" onClick={applyDetected} disabled={busy}><CheckCircle size={13} /> {rec.pair && rec.pair.other_document_id ? 'Für beide Seiten übernehmen' : 'Gelesenes Datum übernehmen'}</button>
        )}
        {canManage && (manualOpen ? (
          <div className="ablauf-manual">
            <input type="date" value={manual} onChange={(event) => setManual(event.target.value)} aria-label="Ablaufdatum von Hand" />
            <button className="is-primary" onClick={saveManual} disabled={busy || !manual}>Speichern</button>
            <button className="is-neutral" onClick={() => { setManualOpen(false); setManual(''); }}>Abbrechen</button>
          </div>
        ) : (
          <button className="is-neutral" onClick={() => setManualOpen(true)} disabled={busy}>Datum von Hand</button>
        ))}
        {canManage && <button className="is-neutral" onClick={reread} disabled={busy}><ScanSearch size={13} /> Neu lesen</button>}
        <button className="is-neutral" onClick={() => onOpen(item)}><Eye size={13} /> Prüfbereich</button>
      </div>
    </article>
  );
}

export default function Ablaufdaten() {
  const { can } = useAuth();
  const canManage = !!(can && can('freigaben.manage'));
  const inbox = useReviewInbox();
  const registry = useRef(null);
  if (!registry.current) registry.current = createBlobRegistry();
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [group, setGroup] = useState('mismatch');
  const [limit, setLimit] = useState(30);
  const [starting, setStarting] = useState(false);
  // Prüfbereich öffnet sich HIER im Reiter (kein Sprung nach „Dokumente"); beim Schließen bleibt Gruppe + Liste stehen.
  const [reviewDoc, setReviewDoc] = useState(null);
  // FAHRER-KI-SPERRE-HANDELN-001: ?driver=<id> (aus „Datum prüfen" an der Sperre) zeigt nur diesen Fahrer.
  const [searchParams, setSearchParams] = useSearchParams();
  const driverFilter = searchParams.get('driver') || null;

  const load = useCallback(() => {
    (driverFilter ? api.get('/admin/document-recognition/overview', { params: { driver_id: driverFilter } }) : api.get('/admin/document-recognition/overview'))
      .then((response) => { setData(response.data); setLoadError(false); })
      .catch(() => { setData(null); setLoadError(true); });
  }, [driverFilter]);

  useEffect(() => { load(); return () => registry.current.revokeAll(); }, [load]);

  // Läuft ein Bestandslauf, alle 3 s nachsehen (Fortschritt), danach die Liste neu laden.
  const running = !!data?.run?.running;
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(() => {
      api.get('/admin/document-recognition/run').then((response) => {
        setData((current) => (current ? { ...current, run: response.data.run } : current));
        if (!response.data.run.running) load();
      }).catch(() => {});
    }, 3000);
    return () => clearInterval(t);
  }, [running, load]);

  const startRun = async () => {
    setStarting(true);
    try {
      const response = await api.post('/admin/document-recognition/run', { limit: 300 });
      toast.success(response.data.started ? 'Bestand wird nachgelesen …' : 'Ein Lauf läuft bereits');
      setData((current) => (current ? { ...current, run: response.data.run } : current));
    } catch (error) {
      toast.error(error.response?.data?.error || 'Lauf konnte nicht gestartet werden');
    } finally { setStarting(false); }
  };

  const items = data?.items || [];
  const counts = useMemo(() => {
    const out = {};
    for (const g of GROUPS) out[g.key] = items.filter((item) => g.statuses.includes(item.recognition.status)).length;
    return out;
  }, [items]);
  const active = GROUPS.find((g) => g.key === group) || GROUPS[0];
  // Beim Einstieg über ?driver= EINMAL die erste nicht-leere Gruppe wählen; danach bleibt jede bewusste Gruppenwahl stehen.
  const autoGroupDone = useRef(false);
  useEffect(() => {
    if (!driverFilter || !data || autoGroupDone.current) return;
    autoGroupDone.current = true;
    if ((counts[group] || 0) === 0) { const first = GROUPS.find((g) => counts[g.key] > 0); if (first) setGroup(first.key); }
  }, [driverFilter, data, counts, group]);
  const visible = items.filter((item) => active.statuses.includes(item.recognition.status));
  const shown = visible.slice(0, limit);
  const extraction = data?.extraction;
  const run = data?.run;

  return (
    <div className="ablauf-page">
      {reviewDoc && <ReviewDrawer key={reviewDoc.id} doc={reviewDoc} onClose={() => { setReviewDoc(null); load(); }} onDecided={(id) => { try { inbox.documentDecided(id); } catch (_) {} load(); }} />}
      <PageHeader
        title="Ablaufdaten prüfen"
        description="Was steht auf dem Dokument, was ist eingetragen? Übernommen wird ein Datum nur per Klick — jede Änderung wird protokolliert, der Ablauf-Wächter rechnet sofort neu."
        actions={
          <div className="ablauf-head-actions">
            {extraction && (
              <span className={extraction.ok ? 'is-ok' : 'is-off'}>
                {extraction.ok ? <ShieldCheck size={12} /> : <AlertTriangle size={12} />}
                {extraction.ok ? `Erkennung an · ${extraction.model}` : extraction.reason === 'disabled' ? 'Erkennung abgeschaltet' : 'Erkennung nicht verfügbar'}
              </span>
            )}
            {run && <span><CalendarCheck size={12} /> {run.open ?? 0} noch nicht gelesen</span>}
            {canManage && <button onClick={startRun} disabled={starting || running || !(extraction && extraction.ok)}><Play size={13} /> {running ? 'Lauf läuft …' : 'Bestand nachlesen'}</button>}
            <button onClick={load}><RefreshCw size={13} /> Aktualisieren</button>
          </div>
        }
      />

      {run && (run.running || run.summary || run.error) && (
        <section className={`ablauf-run ${run.running ? 'is-running' : ''}`}>
          <span className="ablauf-overline">Bestandslauf</span>
          {run.running ? (
            <p>Liest … {run.progress?.done ?? 0} von {run.progress?.total ?? '?'} Dokumenten. Gesperrte Fahrer und abgelaufene Dokumente zuerst.</p>
          ) : run.error ? (
            <p>Lauf abgebrochen: {run.error}</p>
          ) : (
            <p>
              Zuletzt {fmtTime(run.finished_at)}: {run.summary.read} gelesen · {run.summary.ok} mit Ergebnis · {run.summary.unreadable} ohne Datum · {run.summary.errors} Fehler
              {run.summary.stopped ? ` · gestoppt (${run.summary.stopped})` : ''} · Token {run.summary.input_tokens}/{run.summary.output_tokens}
            </p>
          )}
        </section>
      )}

      {driverFilter && (
        <section className="ablauf-filter" data-testid="driver-filter">
          <span>Nur ein Fahrer{items[0] && items[0].driver ? `: ${items[0].driver.name}` : ''} — alle Gruppen dieses Fahrers</span>
          <button onClick={() => { searchParams.delete('driver'); setSearchParams(searchParams, { replace: true }); }}>Alle Fahrer anzeigen</button>
        </section>
      )}
      <section className="ablauf-groups" aria-label="Gruppen">
        {GROUPS.map((g) => (
          <button key={g.key} className={`${group === g.key ? 'is-active' : ''} is-${g.tone}`} onClick={() => { setGroup(g.key); setLimit(30); }}>
            <span><strong>{g.label}</strong><small>{g.hint}</small></span>
            <b>{counts[g.key] || 0}</b>
          </button>
        ))}
      </section>

      <section className="ablauf-list">
        <header>
          <div><span className="ablauf-overline">{active.label}</span><h2>{visible.length} {visible.length === 1 ? 'Dokument' : 'Dokumente'}</h2></div>
          <span><ShieldCheck size={12} /> Bilder werden autorisiert geladen, kein Token-Link</span>
        </header>
        {loadError ? (
          <ErrorBox text="Übersicht konnte nicht geladen werden — die Liste ist NICHT leer, sondern nicht erreichbar." onRetry={load} />
        ) : !data ? (
          <div className="ablauf-empty">Lädt …</div>
        ) : shown.length === 0 ? (
          <div className="ablauf-empty"><CheckCircle size={20} /><strong>Nichts in dieser Gruppe</strong></div>
        ) : shown.map((item) => (
          <Row key={item.document.id} item={item} registry={registry.current} canManage={canManage} onChanged={load} onOpen={(it) => setReviewDoc({ ...it.document, owner_type: 'driver', owner_id: it.driver.id, owner_name: it.driver.name, recognition: it.recognition })} />
        ))}
        {visible.length > shown.length && (
          <footer className="ablauf-more">
            <span>{shown.length} von {visible.length} sichtbar</span>
            <button onClick={() => setLimit((l) => l + 30)}>Weitere anzeigen</button>
          </footer>
        )}
      </section>
    </div>
  );
}
